import { Box, Stack, Typography, Button, IconButton, Skeleton, Divider } from '@mui/material';
import { useSnackbar } from 'notistack';

import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import DuplicateCopyIcon from 'modules/common/components/Icons/DuplicateCopyIcon';
import useReferralPromoConfig from 'modules/common/hooks/useReferralPromoConfig';

interface ReferralHeroProps {
    copyUrl: string | null;
    referralCode: string | null;
    isLoading: boolean;
}

// Hero container width at which the layout switches from stacked (mobile) to side-by-side
// (desktop). Driven by the container's width rather than the viewport's, so it reacts to the
// side nav opening/closing, not just window resizing.
const HERO_ROW_MIN_WIDTH = 770;
const HERO_ROW_QUERY = `@container (min-width: ${HERO_ROW_MIN_WIDTH}px)`;

// Codes are variable length but the panel width is fixed, so step the font down for longer codes.
const CODE_VARIANT_BY_MAX_LENGTH = [
    { maxLength: 10, variant: 'h2' },
    { maxLength: 14, variant: 'h3' },
] as const;

// Doubles as the button label and the tracked cta_text, so the two can't drift apart.
const COPY_CODE_CTA_TEXT = 'Copy promo code';
const COPY_LINK_CTA_TEXT = 'Or copy your personal link';

export default function ReferralHero({ copyUrl, referralCode, isLoading }: ReferralHeroProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { discountLabel, scorpReward, llcReward } = useReferralPromoConfig();
    const { sendTrackingEvent } = useEventTracking();

    const copyToClipboard = async (value: string | null, successMessage: string) => {
        if (!value) {
            return false;
        }
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar(successMessage, { variant: 'success' });
            return true;
        } catch {
            enqueueSnackbar('Failed to copy', { variant: 'error' });
            return false;
        }
    };

    const handleCopyCode = async () => {
        if (await copyToClipboard(referralCode, 'Promo code copied!')) {
            sendTrackingEvent('Referral Code Copied', {
                category: 'Referral Page',
                action: 'copy_code',
                cta_text: COPY_CODE_CTA_TEXT,
            });
        }
    };

    const handleCopyLink = async () => {
        if (await copyToClipboard(copyUrl, 'Referral link copied!')) {
            sendTrackingEvent('Referral Link Copied', {
                category: 'Referral Page',
                action: 'copy_link',
                cta_text: COPY_LINK_CTA_TEXT,
            });
        }
    };

    const displayedCode = referralCode ?? '-';
    const codeVariant =
        CODE_VARIANT_BY_MAX_LENGTH.find(({ maxLength }) => displayedCode.length <= maxLength)?.variant ?? 'h4';

    return (
        <Box sx={{ width: '100%', containerType: 'inline-size', bgcolor: 'common.white', borderRadius: 3, py: 3 }}>
            <Stack spacing={3.5}>
                {/* Headline — full-width, 2 lines */}
                <Typography variant="h1" color="common.black">
                    Know another business owner?
                    <br />
                    Share Collective and get up to ${scorpReward} 🎁
                </Typography>

                {/* Row: left text column + right promo panel, vertically aligned.
                    Stacks/un-stacks based on the hero's own width via a container query. */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        alignItems: 'stretch',
                        gap: 3.5,
                        [HERO_ROW_QUERY]: {
                            flexDirection: 'row',
                            alignItems: 'center',
                        },
                    }}>
                    {/* Left — "How it works", fluid with container size */}
                    <Stack spacing={3.5} sx={{ flex: 1, minWidth: 0 }}>
                        <div>
                            <Typography variant="h3" fontWeight="bold" lineHeight="28px">
                                How it works
                            </Typography>
                            <Typography variant="h3" fontWeight="regular" lineHeight="28px">
                                Share your promo code. When someone signs up using it, you both get rewarded
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="h3" fontWeight="bold" lineHeight="28px">
                                They get
                            </Typography>
                            <Typography variant="h3" fontWeight="regular" lineHeight="28px">
                                {discountLabel} on any tier
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="h3" fontWeight="bold" lineHeight="28px">
                                You get
                            </Typography>
                            <Typography variant="h3" fontWeight="regular" lineHeight="28px">
                                ${scorpReward} when they choose S Corp
                            </Typography>
                            <Typography variant="h3" fontWeight="regular" lineHeight="28px">
                                ${llcReward} when they choose LLC
                            </Typography>
                        </div>
                    </Stack>

                    {/* Right — promo-code panel */}
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: '100%',
                            [HERO_ROW_QUERY]: {
                                width: 408,
                                minHeight: 280,
                            },
                            bgcolor: 'purple.50',
                            borderRadius: 3,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 2,
                        }}>
                        <Typography variant="h3" fontWeight="regular" lineHeight="28px">
                            Your promo code
                        </Typography>

                        <Stack spacing={2} sx={{ width: '100%', [HERO_ROW_QUERY]: { width: 360 } }}>
                            {/* Code display + Copy button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    height: 56,
                                    px: 2,
                                    bgcolor: 'common.white',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                }}>
                                <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                                    {isLoading ? (
                                        <Skeleton width={133} height={28} />
                                    ) : (
                                        <Typography variant={codeVariant} color="common.black" noWrap>
                                            {displayedCode}
                                        </Typography>
                                    )}
                                </Box>
                                <IconButton
                                    color="primary"
                                    size="small"
                                    aria-label={COPY_CODE_CTA_TEXT}
                                    onClick={handleCopyCode}
                                    disabled={isLoading || !referralCode}>
                                    <DuplicateCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Divider sx={{ borderColor: 'grey.100' }} />

                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                fullWidth
                                onClick={handleCopyLink}
                                disabled={isLoading || !copyUrl}>
                                {COPY_LINK_CTA_TEXT}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
}
